import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, RefreshCw, Search, Truck, Image as ImageIcon, X, Download,
  ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, Calendar, AlertCircle,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';
import apiClient from '@/services/apiClient';

const fmt = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-IN', {
      day: '2-digit', month: 'short', year: '2-digit', hour: '2-digit', minute: '2-digit',
    });
  } catch { return iso; }
};

const defaultFromDate = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`;
};
const defaultToDate = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
};

const TRANSFER_TYPE_FILTERS = [
  { value: 'all', label: 'All transfer types' },
  { value: 'internal_transfer', label: 'Internal Transfer' },
  { value: 'job_worker', label: 'Job Worker' },
];

const prettyStatus = (s) => (s || 'unknown').replace(/_/g, ' ');

const statusBadge = (s) => {
  const v = (s || '').toLowerCase();
  if (['completed', 'submitted', 'dispatched'].includes(v)) {
    return <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100 capitalize">{prettyStatus(s)}</Badge>;
  }
  if (['draft', 'pending'].includes(v)) {
    return <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100 capitalize">{prettyStatus(s)}</Badge>;
  }
  return <Badge variant="outline" className="capitalize">{prettyStatus(s) || '—'}</Badge>;
};

const transferBadge = (t) => {
  if (t === 'internal_transfer') return <Badge className="bg-sky-100 text-sky-700 hover:bg-sky-100">Internal Transfer</Badge>;
  if (t === 'job_worker') return <Badge className="bg-violet-100 text-violet-700 hover:bg-violet-100">Job Worker</Badge>;
  return <Badge variant="outline" className="capitalize">{prettyStatus(t) || '—'}</Badge>;
};

const OutwardGateEntryDetails = ({ type = 'regular' }) => {
  const navigate = useNavigate();
  const isCustomer = type === 'customer';
  const title = isCustomer ? 'Gate Entry Details — Customer Outward' : 'Gate Entry Details — Regular Outward';

  const [rows, setRows] = useState([]);
  const [statusCounts, setStatusCounts] = useState({});
  const [loading, setLoading] = useState(false);

  const [search, setSearch] = useState('');
  const [searchDebounced, setSearchDebounced] = useState('');
  const [transferType, setTransferType] = useState('all');
  const [fromDate, setFromDate] = useState(defaultFromDate());
  const [toDate, setToDate] = useState(defaultToDate());

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  const [exporting, setExporting] = useState(false);

  const [imgEntry, setImgEntry] = useState(null);
  const [imgUrls, setImgUrls] = useState([]);
  const [imgLoading, setImgLoading] = useState(false);

  useEffect(() => {
    const id = setTimeout(() => setSearchDebounced(search.trim()), 400);
    return () => clearTimeout(id);
  }, [search]);

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const params = { type, page, page_size: pageSize };
      if (searchDebounced) params.search = searchDebounced;
      if (!isCustomer && transferType !== 'all') params.transfer_type = transferType;
      if (fromDate) params.from_date = `${fromDate}T00:00:00`;
      if (toDate) params.to_date = `${toDate}T23:59:59`;
      const r = await apiClient.get('/accounts/outward-gate-entries', { params });
      setRows(r.data.rows || []);
      setStatusCounts(r.data.status_counts || {});
      setTotalCount(r.data.total_count || 0);
      setTotalPages(r.data.total_pages || 0);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load outward gate entries');
    } finally {
      setLoading(false);
    }
  }, [type, isCustomer, searchDebounced, transferType, fromDate, toDate, page, pageSize]);

  useEffect(() => { fetchRows(); }, [fetchRows]);
  useEffect(() => { setPage(1); }, [searchDebounced, transferType, fromDate, toDate, pageSize, type]);

  const exportCsv = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams({ type, limit: '50000' });
      if (searchDebounced) params.append('search', searchDebounced);
      if (!isCustomer && transferType !== 'all') params.append('transfer_type', transferType);
      if (fromDate) params.append('from_date', `${fromDate}T00:00:00`);
      if (toDate) params.append('to_date', `${toDate}T23:59:59`);
      const r = await apiClient.get(`/accounts/outward-gate-entries/export.csv?${params.toString()}`, { responseType: 'blob' });
      const blob = new Blob([r.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const now = new Date();
      const stamp = `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}_${String(now.getHours()).padStart(2, '0')}${String(now.getMinutes()).padStart(2, '0')}`;
      a.download = `outward-gate-entries_${type}_${stamp}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('CSV exported');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to export CSV');
    } finally {
      setExporting(false);
    }
  };

  const openImageDialog = async (entryId) => {
    setImgEntry(entryId);
    setImgUrls([]);
    setImgLoading(true);
    try {
      const r = await apiClient.get(`/accounts/outward-gate-entries/${entryId}/signed-images`);
      setImgUrls(r.data.signed_urls || []);
    } catch (e) {
      toast.error('Failed to load images');
    } finally {
      setImgLoading(false);
    }
  };

  const colCount = isCustomer ? 10 : 9;

  return (
    <div className="min-h-screen bg-[#FFFBF5]">
      <header className="bg-[#1A1F2E] text-[#F9FAFB] shadow-lg">
        <div className="max-w-[1600px] mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate('/sections/accounts')} className="text-[#F9FAFB] hover:bg-white/10" data-testid="back-to-accounts">
              <ArrowLeft className="w-4 h-4 mr-1" /> Back
            </Button>
            <div>
              <h1 className="text-xl font-bold">{title}</h1>
              <p className="text-xs text-stone-300">Accounts · Outward gate tracking (view only)</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" onClick={exportCsv} disabled={exporting || loading} className="text-[#F9FAFB] hover:bg-white/10" data-testid="export-csv-btn">
              <Download className={`w-4 h-4 mr-1 ${exporting ? 'animate-pulse' : ''}`} />
              {exporting ? 'Exporting…' : 'Export CSV'}
            </Button>
            <Button variant="ghost" onClick={fetchRows} disabled={loading} className="text-[#F9FAFB] hover:bg-white/10" data-testid="refresh-btn">
              <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-[1600px] mx-auto px-4 md:px-6 py-6 space-y-4">
        {/* Counters */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="status-counters">
          <Card>
            <CardContent className="py-4">
              <p className="text-xs uppercase tracking-wide text-stone-500">Total (filtered)</p>
              <p className="text-2xl font-semibold text-[#1A1F2E]" data-testid="counter-total">{totalCount}</p>
            </CardContent>
          </Card>
          {Object.entries(statusCounts).slice(0, 3).map(([s, n]) => (
            <Card key={s}>
              <CardContent className="py-4">
                <p className="text-xs uppercase tracking-wide text-stone-500 capitalize">{prettyStatus(s)}</p>
                <p className="text-2xl font-semibold text-[#2D5F4F]">{n}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <Truck className="w-5 h-5 text-[#2D5F4F]" /> {isCustomer ? 'Customer Outward Entries' : 'Regular Outward Entries'}
            </CardTitle>
            <CardDescription>
              {isCustomer
                ? 'Every customer outward gate pass — boxes, quantities, driver & transport details. View / report only.'
                : 'Every regular outward gate entry (internal transfer & job worker) — destination, bundles, vehicle & transport. View / report only.'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-6 gap-3 mb-4">
              {!isCustomer && (
                <div>
                  <Label className="text-xs">Transfer Type</Label>
                  <Select value={transferType} onValueChange={setTransferType}>
                    <SelectTrigger data-testid="filter-transfer-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {TRANSFER_TYPE_FILTERS.map((t) => (
                        <SelectItem key={t.value} value={t.value} data-testid={`filter-transfer-type-${t.value}`}>{t.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div>
                <Label className="text-xs flex items-center gap-1"><Calendar className="w-3 h-3" /> From</Label>
                <Input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} className="h-9" data-testid="filter-from-date" />
              </div>
              <div>
                <Label className="text-xs flex items-center gap-1"><Calendar className="w-3 h-3" /> To</Label>
                <Input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} className="h-9" data-testid="filter-to-date" />
              </div>
              <div className={isCustomer ? 'md:col-span-4' : 'md:col-span-3'}>
                <Label className="text-xs">{isCustomer ? 'Search (gate pass, customer, vehicle, driver)' : 'Search (document, destination, vehicle, staff)'}</Label>
                <div className="relative">
                  <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
                  <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={isCustomer ? 'GP2026… or customer name…' : 'DOC000123/26-27 or destination…'} className="pl-8" data-testid="search-input" />
                </div>
              </div>
            </div>

            <div className="border rounded-lg overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    {isCustomer ? (
                      <>
                        <TableHead className="whitespace-nowrap">Day</TableHead>
                        <TableHead>Customer</TableHead>
                        <TableHead>Transport</TableHead>
                        <TableHead className="min-w-[150px]">Gate Pass(es)</TableHead>
                        <TableHead>Boxes</TableHead>
                        <TableHead>Total Qty</TableHead>
                        <TableHead>Weight (kg)</TableHead>
                        <TableHead>Driver(s)</TableHead>
                        <TableHead>Vehicle(s)</TableHead>
                        <TableHead>Status</TableHead>
                      </>
                    ) : (
                      <>
                        <TableHead className="min-w-[150px]">Document</TableHead>
                        <TableHead>Destination</TableHead>
                        <TableHead>Transfer Type</TableHead>
                        <TableHead>Bundles</TableHead>
                        <TableHead>Vehicle</TableHead>
                        <TableHead>Transport</TableHead>
                        <TableHead>Staff</TableHead>
                        <TableHead>Gate</TableHead>
                        <TableHead>Status</TableHead>
                      </>
                    )}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading && rows.length === 0 ? (
                    <TableRow><TableCell colSpan={colCount + 1} className="text-center py-12 text-stone-500">
                      <RefreshCw className="w-5 h-5 animate-spin inline mr-2" /> Loading…
                    </TableCell></TableRow>
                  ) : rows.length === 0 ? (
                    <TableRow><TableCell colSpan={colCount + 1} className="text-center py-12 text-stone-400">
                      No entries match the current filters.
                    </TableCell></TableRow>
                  ) : (
                    rows.map((r) => (
                      <TableRow key={r.entry_id} data-testid={`outward-row-${r.entry_id}`}>
                        {isCustomer ? (
                          <>
                            <TableCell className="text-sm whitespace-nowrap font-medium">{r.day || '—'}</TableCell>
                            <TableCell className="text-sm max-w-[240px] truncate font-medium" title={r.customer}>{r.customer || '—'}</TableCell>
                            <TableCell className="text-sm">{r.transport_name || '—'}</TableCell>
                            <TableCell className="text-sm font-mono max-w-[220px] truncate" title={r.gate_pass_number}>{r.gate_pass_number || '—'}</TableCell>
                            <TableCell className="text-sm">{r.total_boxes ?? '—'}</TableCell>
                            <TableCell className="text-sm">{r.total_qty ?? '—'}</TableCell>
                            <TableCell className="text-sm">{r.total_weight_kg ?? '—'}</TableCell>
                            <TableCell className="text-sm max-w-[180px] truncate" title={r.driver_name}>
                              <div>{r.driver_name || '—'}</div>
                              {r.driver_contact && <div className="text-[10px] text-stone-400">{r.driver_contact}</div>}
                            </TableCell>
                            <TableCell className="text-sm font-mono max-w-[160px] truncate" title={r.vehicle_no}>{r.vehicle_no || '—'}</TableCell>
                            <TableCell>{statusBadge(r.status)}</TableCell>
                          </>
                        ) : (
                          <>
                            <TableCell>
                              <div className="font-mono text-sm">{r.document_barcode || '—'}</div>
                              <div className="text-[10px] text-stone-400">{r.entry_id}</div>
                              {r.image_count > 0 && (
                                <button
                                  onClick={() => openImageDialog(r.entry_id)}
                                  className="mt-1 inline-flex items-center gap-1 text-xs text-indigo-600 hover:underline"
                                  data-testid={`view-images-${r.entry_id}`}
                                >
                                  <ImageIcon className="w-3 h-3" /> {r.image_count} image{r.image_count > 1 ? 's' : ''}
                                </button>
                              )}
                            </TableCell>
                            <TableCell className="text-sm max-w-[220px] truncate" title={r.destination}>{r.destination || '—'}</TableCell>
                            <TableCell>{transferBadge(r.transfer_type)}</TableCell>
                            <TableCell className="text-sm">{r.bundle_count ?? '—'}</TableCell>
                            <TableCell className="text-sm font-mono">{r.vehicle_no || '—'}</TableCell>
                            <TableCell className="text-sm">{r.transport_name || '—'}</TableCell>
                            <TableCell className="text-sm">{r.staff_id || '—'}</TableCell>
                            <TableCell className="text-xs whitespace-nowrap">{fmt(r.gate_at)}</TableCell>
                            <TableCell>{statusBadge(r.status)}</TableCell>
                          </>
                        )}
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>

            {/* Pagination footer */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mt-3 text-sm">
              <div className="text-stone-600">
                {loading ? 'Loading…' : (
                  totalCount === 0 ? 'No results' : (
                    <>
                      Showing <span className="font-semibold">{(page - 1) * pageSize + 1}</span>
                      &nbsp;–&nbsp;
                      <span className="font-semibold">{Math.min(page * pageSize, totalCount)}</span>
                      &nbsp;of <span className="font-semibold">{totalCount}</span> entries
                      &nbsp;·&nbsp; page {page} / {totalPages || 1}
                    </>
                  )
                )}
              </div>
              <div className="flex items-center gap-2">
                <Label className="text-xs text-stone-500 m-0">Rows per page</Label>
                <Select value={String(pageSize)} onValueChange={(v) => setPageSize(parseInt(v, 10) || 50)}>
                  <SelectTrigger className="h-9 w-[80px]" data-testid="page-size-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {[25, 50, 100, 200].map((n) => (
                      <SelectItem key={n} value={String(n)} data-testid={`page-size-${n}`}>{n}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button variant="outline" size="icon" className="h-9 w-9" onClick={() => setPage(1)} disabled={loading || page <= 1} data-testid="page-first"><ChevronsLeft className="w-4 h-4" /></Button>
                <Button variant="outline" size="icon" className="h-9 w-9" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={loading || page <= 1} data-testid="page-prev"><ChevronLeft className="w-4 h-4" /></Button>
                <Button variant="outline" size="icon" className="h-9 w-9" onClick={() => setPage((p) => Math.min(totalPages || 1, p + 1))} disabled={loading || page >= (totalPages || 1)} data-testid="page-next"><ChevronRight className="w-4 h-4" /></Button>
                <Button variant="outline" size="icon" className="h-9 w-9" onClick={() => setPage(totalPages || 1)} disabled={loading || page >= (totalPages || 1)} data-testid="page-last"><ChevronsRight className="w-4 h-4" /></Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </main>

      {/* Image preview dialog (regular outward only) */}
      <Dialog open={!!imgEntry} onOpenChange={(o) => !o && setImgEntry(null)}>
        <DialogContent className="max-w-3xl" data-testid="image-preview-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <span className="text-sm font-mono">{imgEntry}</span>
              <Button variant="ghost" size="icon" onClick={() => setImgEntry(null)}><X className="w-4 h-4" /></Button>
            </DialogTitle>
          </DialogHeader>
          {imgLoading ? (
            <div className="py-12 text-center text-stone-500"><RefreshCw className="w-5 h-5 animate-spin inline mr-2" /> Loading images…</div>
          ) : imgUrls.length === 0 ? (
            <div className="py-8 text-center text-stone-400"><AlertCircle className="w-5 h-5 inline mr-2" /> No images available.</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-h-[70vh] overflow-y-auto">
              {imgUrls.map((u, i) => (
                u ? (
                  <a key={i} href={u} target="_blank" rel="noopener noreferrer" className="block border rounded overflow-hidden hover:shadow-md transition" data-testid={`image-thumb-${i}`}>
                    <img src={u} alt={`Doc image ${i + 1}`} className="w-full h-auto" />
                  </a>
                ) : (
                  <div key={i} className="p-6 border rounded text-center text-xs text-stone-400">Failed to load image #{i + 1}</div>
                )
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default OutwardGateEntryDetails;
